/**
 * Continuous review marquee — specialized Swiper (freeMode + delay 0).
 * Not using <product-carousel> (see shopify-section-layout exceptions).
 *
 * Hover pause: autoplay.stop() alone does not freeze an in-flight freeMode
 * transition — we snapshot translate and setTransition(0).
 *
 * data-mobile-layout="stack"  → destroy Swiper below 601px (CSS tiles cards)
 * data-mobile-layout="scroll" → keep Swiper on mobile
 */
if (!customElements.get('anniversary-reviews')) {
  class AnniversaryReviews extends HTMLElement {
    connectedCallback() {
      this._tries = 0;
      this._paused = false;
      this._mobileLayout = this.dataset.mobileLayout || 'stack';
      this._mq = window.matchMedia('(min-width: 601px)');
      this._onMq = () => this._sync();
      this._onEnter = () => this._pause();
      this._onLeave = () => this._resume();

      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._onMq);
      } else {
        this._mq.addListener(this._onMq);
      }

      this._sync();
    }

    disconnectedCallback() {
      this._unbindHover();
      if (this._mq) {
        if (this._mq.removeEventListener) {
          this._mq.removeEventListener('change', this._onMq);
        } else {
          this._mq.removeListener(this._onMq);
        }
      }
      this._destroy();
    }

    _shouldInit() {
      return this._mq.matches || this._mobileLayout === 'scroll';
    }

    _hoverTarget() {
      return this.querySelector('.anniversary-reviews__carousel') || this;
    }

    _bindHover() {
      this._unbindHover();
      this._hoverEl = this._hoverTarget();
      this._hoverEl.addEventListener('mouseenter', this._onEnter);
      this._hoverEl.addEventListener('mouseleave', this._onLeave);
    }

    _unbindHover() {
      if (!this._hoverEl) return;
      this._hoverEl.removeEventListener('mouseenter', this._onEnter);
      this._hoverEl.removeEventListener('mouseleave', this._onLeave);
      this._hoverEl = null;
    }

    _pause() {
      if (!this.swiper || this._paused) return;
      this._paused = true;
      this.swiper.autoplay?.stop?.();
      const translate = this.swiper.getTranslate();
      this.swiper.setTranslate(translate);
      this.swiper.setTransition(0);
      // freeMode freeze can leave animating=true and block autoplay restart
      this.swiper.animating = false;
    }

    _resume() {
      if (!this.swiper || !this._paused) return;
      this._paused = false;
      this.swiper.animating = false;
      // Kick continuous marquee, then let autoplay keep it going
      this.swiper.slideNext();
      this.swiper.autoplay?.start?.();
    }

    _sync() {
      if (this._shouldInit()) {
        this._initWhenReady();
      } else {
        this._unbindHover();
        this._destroy();
      }
    }

    _destroy() {
      this._paused = false;
      this.swiper?.destroy?.(true, true);
      this.swiper = null;
    }

    _initWhenReady() {
      if (typeof Swiper === 'undefined') {
        this._tries += 1;
        if (this._tries > 80) return;
        requestAnimationFrame(() => this._initWhenReady());
        return;
      }
      this._init();
    }

    _init() {
      if (this.swiper) return;

      const container = this.querySelector('[data-reviews-swiper]');
      if (!container) return;

      const nextEl = this.querySelector('.swiper-btn-next');
      const prevEl = this.querySelector('.swiper-btn-prev');

      this.swiper = new Swiper(container, {
        slidesPerView: 1.5,
        slidesPerGroup: 1,
        spaceBetween: 10,
        loop: true,
        // Higher = slower continuous marquee
        speed: 10000,
        freeMode: true,
        autoplay: {
          delay: 0,
          stopOnLastSlide: false,
          disableOnInteraction: false,
        },
        updateOnWindowResize: true,
        breakpoints: {
          750: {
            slidesPerView: 2,
            spaceBetween: 15,
            slidesPerGroup: 1,
          },
          1000: {
            slidesPerView: 2.5,
            spaceBetween: 15,
            slidesPerGroup: 1,
          },
          1400: {
            slidesPerView: 4,
            spaceBetween: 15,
            slidesPerGroup: 2,
          },
        },
        navigation: {
          nextEl: nextEl || null,
          prevEl: prevEl || null,
        },
      });

      this._bindHover();
    }
  }

  customElements.define('anniversary-reviews', AnniversaryReviews);
}
